import React, { useState, useEffect } from 'react';
import { Search, User, Film, Tv, BookOpen, Heart, Users, Star, Users2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { searchMulti } from '../services/tmdb';
import { Input } from './ui/input';

const Header = ({ onSearch, onFilter, activeFilter, user }) => {
  const [scrolled, setScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const results = await searchMulti(searchQuery);
      onSearch(results, searchQuery);
    }
  };

  const navItems = [
    { id: null, label: 'Home', icon: null },
    { id: 'movies', label: 'Films', icon: Film },
    { id: 'tv', label: 'Discovery', icon: Tv },
    { id: 'diary', label: 'Chronicle', icon: BookOpen },
    { id: 'mylist', label: 'Watchlist', icon: Heart },
    { id: 'members', label: 'Members', icon: Users },
    { id: 'reviews', label: 'Reviews', icon: Star },
    { id: 'clubs', label: 'Clubs', icon: Users2 },
  ];

  const handleNavClick = (filterId) => {
    setSearchQuery('');
    setShowSearch(false);
    onSearch([], '');
    
    // Special navigation for certain items
    if (filterId === 'members' || filterId === 'clubs' || filterId === 'diary' || filterId === 'reviews') {
      // Navigate to dashboard for social features
      navigate('/login');
      return;
    }
    
    onFilter && onFilter(filterId);
    navigate('/');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black' : 'bg-gradient-to-b from-black/80 to-transparent'
      }`}
    >
      <div className="flex items-center justify-between px-4 md:px-8 lg:px-16 py-4">
        {/* Logo */}
        <div className="flex items-center gap-6">
          <h1 
            onClick={() => handleNavClick(null)}
            className="text-3xl md:text-4xl tracking-tight cursor-pointer hover:opacity-80 transition-opacity"
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              color: '#E50914',
              letterSpacing: '3px',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
            }}
            data-testid="casitaplex-logo"
          >
            CASITAPLEX
          </h1>
          
          {/* Navigation - Desktop */}
          <nav className="hidden lg:flex items-center gap-4 text-sm">
            {navItems.map((item) => (
              <button
                key={item.id || 'home'}
                onClick={() => handleNavClick(item.id)}
                className={`flex items-center gap-1.5 transition-colors px-2 py-1 rounded ${
                  activeFilter === item.id 
                    ? 'text-white font-semibold bg-white/10' 
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {item.icon && <item.icon className="h-4 w-4" />}
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative">
            {showSearch ? (
              <form onSubmit={handleSearch} className="flex items-center">
                <Input
                  type="text"
                  placeholder="Search films, TV series..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-black/80 border border-white text-white placeholder:text-gray-400 w-48 md:w-64 pr-10"
                  autoFocus
                  onBlur={() => {
                    if (!searchQuery) setShowSearch(false);
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setShowSearch(false);
                    onSearch([], '');
                  }}
                  className="absolute right-3 text-white hover:text-gray-300"
                >
                  <Search className="h-5 w-5" />
                </button>
              </form>
            ) : (
              <button
                onClick={() => setShowSearch(true)}
                className="text-white hover:text-gray-300 transition-colors"
              >
                <Search className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Profile / Sign Up */}
          {user ? (
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-white hover:text-gray-300 transition-colors"
              title="Dashboard"
              data-testid="header-user-profile"
            >
              <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center text-white text-xs font-semibold overflow-hidden">
                {user.profile_picture
                  ? <img src={user.profile_picture} alt="" className="w-full h-full object-cover" />
                  : `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()
                }
              </div>
            </button>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="flex items-center gap-2 text-white hover:text-gray-300 transition-colors bg-red-600 hover:bg-red-700 px-4 py-1.5 rounded-full font-medium"
              data-testid="header-signup-btn"
            >
              <User className="h-4 w-4" />
              <span className="text-sm">Sign Up</span>
            </button>
          )}
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="lg:hidden overflow-x-auto scrollbar-hide border-t border-white/10">
        <nav className="flex items-center gap-1 px-4 py-2 min-w-max">
          {navItems.map((item) => (
            <button
              key={item.id || 'home'}
              onClick={() => handleNavClick(item.id)}
              className={`flex items-center gap-1.5 transition-colors px-3 py-1.5 rounded whitespace-nowrap text-sm ${
                activeFilter === item.id 
                  ? 'text-white font-semibold bg-red-600' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {item.icon && <item.icon className="h-3.5 w-3.5" />}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </div>
    </header>
  );
};

export default Header;
